/**
 * Mettre un document déjà écrit sur un papier à en-tête du cabinet.
 *
 * Le papier à en-tête (« papier » dans le code) est un Word du cabinet dont le serveur a vidé le
 * corps : il ne garde que ses en-têtes, ses pieds de page et sa mise en page
 * (`services/act-personalization/prepareCabinetPaper.ts`). Le geste « Changer de papier à en-tête »
 * du canvas (2026-10-14) garde TOUT le document de l'avocat, et lui greffe ces trois choses.
 *
 * ## Pourquoi greffer le modèle sur le document, et pas l'inverse
 *
 * Poser le corps du document dans le modèle obligerait à transporter tout ce que le corps
 * référence : images, liens, listes numérotées, notes, commentaires, styles. Le modèle, lui, ne
 * transporte que quelques en-têtes et pieds, avec leurs images. Le document garde donc ses
 * styles, sa numérotation et ses révisions suivies, octet pour octet hors de `w:sectPr`.
 *
 * ## Ce qui change dans le document
 *
 * - Ses en-têtes et pieds de page sont retirés (parties, relations, types de contenu), et ceux du
 *   modèle sont copiés sous des noms neufs (`word/modele-de-page-*.xml`), avec leurs relations et
 *   leurs images, renommées pour ne jamais écraser une image du document.
 * - Chaque section (`w:sectPr`, du corps ou d'un paragraphe) reprend la mise en page de la section
 *   finale du modèle : format, marges, références d'en-tête et de pied, page de titre. Le type de
 *   saut de chaque section du document est gardé, sinon ses sauts de section deviendraient tous
 *   des sauts de page.
 * - Les styles qu'utilisent les en-têtes et pieds copiés sont ajoutés quand le document ne les a
 *   pas ; un style que le document porte déjà sous le même NOM Word sert à leur place (même règle
 *   que `documentDeDepart.ts` : deux styles au même rôle font refuser les titres à l'éditeur).
 * - `w:evenAndOddHeaders` (`word/settings.xml`) suit le modèle : sans lui, l'en-tête des pages
 *   paires du modèle ne s'afficherait jamais ; avec celui du document, ses pages paires
 *   resteraient sans en-tête.
 *
 * Le résultat passe ensuite la porte d'intégrité du canvas (`prepareCanvasDocx`) avant d'être
 * monté : une greffe qui laisserait une référence orpheline est refusée là, pas à l'export.
 */
import JSZip from 'jszip';

const NS_W = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const NS_R = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const NS_RELS = 'http://schemas.openxmlformats.org/package/2006/relationships';
const NS_TYPES = 'http://schemas.openxmlformats.org/package/2006/content-types';

const DOCUMENT = 'word/document.xml';
const RELATIONS_DU_DOCUMENT = 'word/_rels/document.xml.rels';
const TYPES_DE_CONTENU = '[Content_Types].xml';
const STYLES = 'word/styles.xml';
const REGLAGES = 'word/settings.xml';

const TYPE_DE_CONTENU: Record<GenreDePartie, string> = {
  header: 'application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml',
  footer: 'application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml',
};

/** Ordre des enfants de `w:sectPr` (ECMA-376, CT_SectPr) : Word refuse un ordre différent. */
const ORDRE_DE_LA_SECTION = [
  'headerReference', 'footerReference', 'footnotePr', 'endnotePr', 'type', 'pgSz', 'pgMar',
  'paperSrc', 'pgBorders', 'lnNumType', 'pgNumType', 'cols', 'formProt', 'vAlign', 'noEndnote',
  'titlePg', 'textDirection', 'bidi', 'rtlGutter', 'docGrid', 'printerSettings', 'sectPrChange',
];

type GenreDePartie = 'header' | 'footer';

/** Le modèle ou le document ne se lit pas comme un Word : rien n'a été modifié. */
export class GreffeDuModeleImpossibleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GreffeDuModeleImpossibleError';
  }
}

interface Relation {
  readonly id: string;
  readonly type: string;
  readonly cible: string;
  readonly externe: boolean;
}

const lire = (xml: string, partie: string): Document => {
  const arbre = new DOMParser().parseFromString(xml, 'application/xml');
  if (arbre.getElementsByTagName('parsererror').length > 0) {
    throw new GreffeDuModeleImpossibleError(`La partie ${partie} n'est pas un XML lisible : la greffe du papier à en-tête est abandonnée.`);
  }
  return arbre;
};

/**
 * Ré-écrit une partie XML avec UNE déclaration.
 *
 * Les sérialiseurs ne s'accordent pas : Chrome réécrit la déclaration du document lu, jsdom
 * l'omet. En ajouter une sans retirer la sienne en mettait deux, et la porte d'intégrité refusait
 * le Word (`DOCX_XML_INVALID … 2:6`, constaté dans l'application le 2026-10-15, vert sous jsdom).
 */
const ecrire = (arbre: Document): string => {
  const serialise = new XMLSerializer().serializeToString(arbre).replace(/^\s*<\?xml[^?]*\?>\s*/, '');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n${serialise}`;
};

async function texteDe(archive: JSZip, chemin: string, role: string): Promise<string> {
  const fichier = archive.file(chemin);
  if (!fichier) throw new GreffeDuModeleImpossibleError(`${role} n'a pas de ${chemin} : ce n'est pas un document Word complet.`);
  return fichier.async('string');
}

/** `word/header1.xml` → `word/_rels/header1.xml.rels`. */
const relationsDe = (chemin: string): string => {
  const coupure = chemin.lastIndexOf('/');
  return `${chemin.slice(0, coupure)}/_rels/${chemin.slice(coupure + 1)}.rels`;
};

/** Résout la cible d'une relation, relative au dossier de la partie qui la porte. */
function cheminDeLaCible(partie: string, cible: string): string {
  if (cible.startsWith('/')) return cible.slice(1);
  const segments = partie.split('/').slice(0, -1);
  for (const morceau of cible.split('/')) {
    if (morceau === '..') segments.pop();
    else if (morceau !== '.' && morceau !== '') segments.push(morceau);
  }
  return segments.join('/');
}

function relationsLues(arbre: Document): Relation[] {
  return [...arbre.getElementsByTagNameNS(NS_RELS, 'Relationship')].map((relation) => ({
    id: relation.getAttribute('Id') ?? '',
    type: relation.getAttribute('Type') ?? '',
    cible: relation.getAttribute('Target') ?? '',
    externe: relation.getAttribute('TargetMode') === 'External',
  }));
}

/** `…/relationships/header` → `header` ; la variante « strict » finit de la même façon. */
const genreDeLaRelation = (type: string): GenreDePartie | null =>
  type.endsWith('/header') ? 'header' : type.endsWith('/footer') ? 'footer' : null;

const enfantsW = (element: Element, nom: string): Element[] =>
  [...element.childNodes].filter(
    (noeud): noeud is Element => noeud.nodeType === 1 && (noeud as Element).namespaceURI === NS_W && (noeud as Element).localName === nom,
  );

/** La section finale du corps : dernier enfant direct de `w:body`. */
function sectionFinale(arbre: Document, role: string): Element {
  const corps = arbre.getElementsByTagNameNS(NS_W, 'body')[0];
  const derniere = corps ? enfantsW(corps, 'sectPr').at(-1) : undefined;
  if (!derniere) throw new GreffeDuModeleImpossibleError(`${role} n'a pas de mise en page finale (w:sectPr du corps) : la greffe du papier à en-tête est abandonnée.`);
  return derniere;
}

/** Toutes les sections du document, hors l'ancienne mise en page d'une révision suivie (`w:sectPrChange`). */
const sectionsDuDocument = (arbre: Document): Element[] =>
  [...arbre.getElementsByTagNameNS(NS_W, 'sectPr')].filter(
    (section) => section.parentElement?.localName !== 'sectPrChange',
  );

function identifiantLibre(pris: Set<string>): string {
  let rang = pris.size + 1;
  while (pris.has(`rIdModele${rang}`)) rang += 1;
  const id = `rIdModele${rang}`;
  pris.add(id);
  return id;
}

function nomLibre(archive: JSZip, base: string, extension: string): string {
  let rang = 1;
  while (archive.file(`${base}${rang}${extension}`)) rang += 1;
  return `${base}${rang}${extension}`;
}

/** Les identifiants de style (`w:pStyle`, `w:rStyle`, `w:tblStyle`) cités par une partie. */
function stylesCites(partie: Document): Element[] {
  return ['pStyle', 'rStyle', 'tblStyle'].flatMap((nom) => [...partie.getElementsByTagNameNS(NS_W, nom)]);
}

const valeurW = (element: Element): string | null => element.getAttributeNS(NS_W, 'val') ?? element.getAttribute('w:val');

interface FeuilleDeStyles {
  readonly arbre: Document;
  readonly parId: Map<string, Element>;
  readonly idParNom: Map<string, string>;
}

function feuilleLue(arbre: Document): FeuilleDeStyles {
  const parId = new Map<string, Element>();
  const idParNom = new Map<string, string>();
  for (const style of arbre.getElementsByTagNameNS(NS_W, 'style')) {
    const id = style.getAttributeNS(NS_W, 'styleId') ?? style.getAttribute('w:styleId');
    if (!id) continue;
    parId.set(id, style);
    const nom = enfantsW(style, 'name')[0];
    const valeur = nom ? valeurW(nom) : null;
    if (valeur) idParNom.set(valeur.trim().toLowerCase(), id);
  }
  return { arbre, parId, idParNom };
}

/**
 * Rend l'identifiant, dans la feuille du document, du style `id` du modèle — en l'y ajoutant au
 * besoin avec sa chaîne `w:basedOn`. `null` : le modèle ne le définit pas non plus ; la référence
 * est laissée telle quelle, Word retombe sur le style par défaut.
 */
function styleDansLeDocument(
  id: string, modele: FeuilleDeStyles, document: FeuilleDeStyles, resolus: Map<string, string | null>,
): string | null {
  const deja = resolus.get(id);
  if (deja !== undefined) return deja;
  if (document.parId.has(id)) {
    resolus.set(id, id);
    return id;
  }
  const source = modele.parId.get(id);
  if (!source) {
    resolus.set(id, null);
    return null;
  }
  const nom = enfantsW(source, 'name')[0];
  const cle = nom ? valeurW(nom)?.trim().toLowerCase() : undefined;
  const homonyme = cle ? document.idParNom.get(cle) : undefined;
  if (homonyme) {
    resolus.set(id, homonyme);
    return homonyme;
  }
  resolus.set(id, id);
  const copie = document.arbre.importNode(source, true) as Element;
  // Un style ajouté n'est jamais le style par défaut : le document a déjà le sien.
  copie.removeAttributeNS(NS_W, 'default');
  copie.removeAttribute('w:default');
  for (const lien of ['basedOn', 'next', 'link'].flatMap((n) => enfantsW(copie, n))) {
    const cible = valeurW(lien);
    const resolu = cible ? styleDansLeDocument(cible, modele, document, resolus) : null;
    if (resolu) lien.setAttributeNS(NS_W, 'w:val', resolu);
    else lien.parentNode?.removeChild(lien);
  }
  document.arbre.documentElement.appendChild(copie);
  document.parId.set(id, copie);
  if (cle) document.idParNom.set(cle, id);
  return id;
}

/** Pose les enfants de `section` dans l'ordre du schéma. */
function ranger(section: Element): void {
  const rang = (noeud: Element): number => {
    const index = ORDRE_DE_LA_SECTION.indexOf(noeud.localName);
    return index === -1 ? ORDRE_DE_LA_SECTION.length : index;
  };
  const enfants = [...section.childNodes].filter((n): n is Element => n.nodeType === 1);
  enfants.sort((a, b) => rang(a) - rang(b));
  for (const enfant of enfants) section.appendChild(enfant);
}

/**
 * Greffe le papier à en-tête `papier` sur `document` et rend les octets du nouveau Word.
 *
 * @param document — le document de l'avocat, tel que le canvas l'exporte.
 * @param papier — le papier à en-tête du cabinet, tel que le serveur le sert (corps vidé).
 * @throws GreffeDuModeleImpossibleError si l'un des deux ne se lit pas comme un Word.
 */
export async function greffeLeModeleDePage(document: ArrayBuffer, papier: ArrayBuffer): Promise<ArrayBuffer> {
  let archive: JSZip;
  let modele: JSZip;
  try {
    // Une vue `Uint8Array` du domaine courant : JSZip reconnaît ses entrées par `instanceof`, et un
    // `ArrayBuffer` venu d'un autre domaine (Blob de Node, iframe) y serait refusé.
    [archive, modele] = await Promise.all([
      JSZip.loadAsync(new Uint8Array(document)), JSZip.loadAsync(new Uint8Array(papier)),
    ]);
  } catch (error) {
    throw new GreffeDuModeleImpossibleError(
      `Le document ou le papier à en-tête n'est pas une archive Word lisible (${error instanceof Error ? error.message : String(error)}).`,
    );
  }

  const corps = lire(await texteDe(archive, DOCUMENT, 'Le document'), DOCUMENT);
  const relations = lire(await texteDe(archive, RELATIONS_DU_DOCUMENT, 'Le document'), RELATIONS_DU_DOCUMENT);
  const types = lire(await texteDe(archive, TYPES_DE_CONTENU, 'Le document'), TYPES_DE_CONTENU);
  const corpsDuModele = lire(await texteDe(modele, DOCUMENT, 'Le papier à en-tête'), `${DOCUMENT} du modèle`);
  const relationsDuModele = relationsLues(lire(await texteDe(modele, RELATIONS_DU_DOCUMENT, 'Le papier à en-tête'), `${RELATIONS_DU_DOCUMENT} du modèle`));
  const typesDuModele = lire(await texteDe(modele, TYPES_DE_CONTENU, 'Le papier à en-tête'), `${TYPES_DE_CONTENU} du modèle`);

  // 1. Retirer les en-têtes et pieds du document : parties, leurs relations, leurs types.
  const racineDesRelations = relations.documentElement;
  for (const relation of [...relations.getElementsByTagNameNS(NS_RELS, 'Relationship')]) {
    if (!genreDeLaRelation(relation.getAttribute('Type') ?? '') || relation.getAttribute('TargetMode') === 'External') continue;
    const partie = cheminDeLaCible(DOCUMENT, relation.getAttribute('Target') ?? '');
    archive.remove(partie);
    archive.remove(relationsDe(partie));
    for (const surcharge of [...types.getElementsByTagNameNS(NS_TYPES, 'Override')]) {
      if (surcharge.getAttribute('PartName') === `/${partie}`) surcharge.parentNode?.removeChild(surcharge);
    }
    racineDesRelations.removeChild(relation);
  }

  // 2. Copier ceux du modèle, sous des noms et des identifiants neufs.
  const idsPris = new Set(relationsLues(relations).map((r) => r.id));
  const idDansLeDocument = new Map<string, string>();
  const partiesCopiees: Document[] = [];
  const cheminsCopies: string[] = [];
  const surcharges = new Map(
    [...typesDuModele.getElementsByTagNameNS(NS_TYPES, 'Override')].map((s) => [s.getAttribute('PartName') ?? '', s.getAttribute('ContentType') ?? '']),
  );
  const extensionsConnues = new Set(
    [...types.getElementsByTagNameNS(NS_TYPES, 'Default')].map((d) => (d.getAttribute('Extension') ?? '').toLowerCase()),
  );
  const extensionsDuModele = new Map(
    [...typesDuModele.getElementsByTagNameNS(NS_TYPES, 'Default')].map((d) => [(d.getAttribute('Extension') ?? '').toLowerCase(), d.getAttribute('ContentType') ?? '']),
  );

  for (const relation of relationsDuModele) {
    const genre = genreDeLaRelation(relation.type);
    if (!genre || relation.externe) continue;
    const source = cheminDeLaCible(DOCUMENT, relation.cible);
    const contenu = modele.file(source);
    if (!contenu) continue;
    const destination = nomLibre(archive, `word/modele-de-page-${genre}`, '.xml');
    // Réservé tout de suite : la partie n'est écrite qu'à la fin (après la greffe des styles), et
    // sans cette réservation le pied suivant recevait le MÊME nom et l'écrasait. Le papier AZAM
    // (deux en-têtes, trois pieds) sortait ainsi avec un seul pied, celui de la première page.
    archive.file(destination, '');
    const partie = lire(await contenu.async('string'), `${source} du modèle`);

    const relationsSource = modele.file(relationsDe(source));
    if (relationsSource) {
      const relationsDeLaPartie = lire(await relationsSource.async('string'), `${relationsDe(source)} du modèle`);
      for (const lien of [...relationsDeLaPartie.getElementsByTagNameNS(NS_RELS, 'Relationship')]) {
        if (lien.getAttribute('TargetMode') === 'External') continue;
        const media = cheminDeLaCible(source, lien.getAttribute('Target') ?? '');
        const octets = modele.file(media);
        if (!octets) {
          lien.parentNode?.removeChild(lien);
          continue;
        }
        const point = media.lastIndexOf('.');
        const extension = point === -1 ? '' : media.slice(point);
        const copie = nomLibre(archive, 'word/media/modele-de-page-', extension);
        archive.file(copie, await octets.async('uint8array'));
        lien.setAttribute('Target', copie.slice('word/'.length));
        const ext = extension.slice(1).toLowerCase();
        const typeDuMedia = extensionsDuModele.get(ext);
        if (ext && !extensionsConnues.has(ext) && typeDuMedia) {
          const defaut = types.createElementNS(NS_TYPES, 'Default');
          defaut.setAttribute('Extension', ext);
          defaut.setAttribute('ContentType', typeDuMedia);
          types.documentElement.insertBefore(defaut, types.documentElement.firstChild);
          extensionsConnues.add(ext);
        }
      }
      archive.file(relationsDe(destination), ecrire(relationsDeLaPartie));
    }

    partiesCopiees.push(partie);
    cheminsCopies.push(destination);
    const id = identifiantLibre(idsPris);
    idDansLeDocument.set(relation.id, id);
    const nouvelle = relations.createElementNS(NS_RELS, 'Relationship');
    nouvelle.setAttribute('Id', id);
    nouvelle.setAttribute('Type', relation.type);
    nouvelle.setAttribute('Target', destination.slice('word/'.length));
    racineDesRelations.appendChild(nouvelle);
    const surcharge = types.createElementNS(NS_TYPES, 'Override');
    surcharge.setAttribute('PartName', `/${destination}`);
    surcharge.setAttribute('ContentType', surcharges.get(`/${source}`) || TYPE_DE_CONTENU[genre]);
    types.documentElement.appendChild(surcharge);
  }

  // 3. Les styles des parties copiées : ajoutés au document, ou renvoyés vers son homonyme.
  const styles = archive.file(STYLES);
  const stylesDuModele = modele.file(STYLES);
  if (styles && stylesDuModele) {
    const feuille = feuilleLue(lire(await styles.async('string'), STYLES));
    const feuilleDuModele = feuilleLue(lire(await stylesDuModele.async('string'), `${STYLES} du modèle`));
    const resolus = new Map<string, string | null>();
    let ajout = false;
    for (const partie of partiesCopiees) {
      for (const reference of stylesCites(partie)) {
        const id = valeurW(reference);
        if (!id) continue;
        const avant = feuille.parId.size;
        const resolu = styleDansLeDocument(id, feuilleDuModele, feuille, resolus);
        if (feuille.parId.size !== avant) ajout = true;
        if (resolu && resolu !== id) reference.setAttributeNS(NS_W, 'w:val', resolu);
      }
    }
    if (ajout) archive.file(STYLES, ecrire(feuille.arbre));
  }
  partiesCopiees.forEach((partie, rang) => {
    // Une liste numérotée dans un en-tête renverrait à la numérotation du MODÈLE, absente du
    // document : la référence serait orpheline. Le paragraphe garde son texte, sans puce.
    for (const numero of [...partie.getElementsByTagNameNS(NS_W, 'numPr')]) numero.parentNode?.removeChild(numero);
    archive.file(cheminsCopies[rang]!, ecrire(partie));
  });

  // 4. Chaque section du document prend la mise en page du modèle.
  const miseEnPage = sectionFinale(corpsDuModele, 'Le papier à en-tête');
  for (const section of sectionsDuDocument(corps)) {
    const typeDeSaut = enfantsW(section, 'type')[0];
    while (section.firstChild) section.removeChild(section.firstChild);
    for (const enfant of [...miseEnPage.childNodes]) {
      if (enfant.nodeType !== 1) continue;
      const element = enfant as Element;
      if (element.localName === 'type' || element.localName === 'sectPrChange') continue;
      const copie = corps.importNode(element, true) as Element;
      if (copie.localName === 'headerReference' || copie.localName === 'footerReference') {
        const ancien = copie.getAttributeNS(NS_R, 'id');
        const nouveau = ancien ? idDansLeDocument.get(ancien) : undefined;
        if (!nouveau) continue;
        copie.setAttributeNS(NS_R, 'r:id', nouveau);
      }
      section.appendChild(copie);
    }
    if (typeDeSaut) section.appendChild(typeDeSaut);
    ranger(section);
  }

  // 5. En-têtes des pages paires : réglage du document entier, il suit le modèle.
  const reglages = archive.file(REGLAGES);
  if (reglages) {
    const arbre = lire(await reglages.async('string'), REGLAGES);
    const reglagesDuModele = modele.file(REGLAGES);
    const pairesDansLeModele = reglagesDuModele
      ? lire(await reglagesDuModele.async('string'), `${REGLAGES} du modèle`).getElementsByTagNameNS(NS_W, 'evenAndOddHeaders').length > 0
      : false;
    const presents = [...arbre.getElementsByTagNameNS(NS_W, 'evenAndOddHeaders')];
    if (pairesDansLeModele && presents.length === 0) {
      // Place du schéma (CT_Settings) : avant `w:defaultTabStop` quand il existe, sinon en fin.
      const reglage = arbre.createElementNS(NS_W, 'w:evenAndOddHeaders');
      const suivant = arbre.getElementsByTagNameNS(NS_W, 'defaultTabStop')[0] ?? null;
      arbre.documentElement.insertBefore(reglage, suivant);
      archive.file(REGLAGES, ecrire(arbre));
    } else if (!pairesDansLeModele && presents.length > 0) {
      for (const reglage of presents) reglage.parentNode?.removeChild(reglage);
      archive.file(REGLAGES, ecrire(arbre));
    }
  }

  archive.file(DOCUMENT, ecrire(corps));
  archive.file(RELATIONS_DU_DOCUMENT, ecrire(relations));
  archive.file(TYPES_DE_CONTENU, ecrire(types));
  return archive.generateAsync({ type: 'arraybuffer' });
}

/**
 * Le papier à en-tête prêt à être montré : deux pages vides, la seconde ouverte par un saut de page.
 *
 * Deux raisons, mesurées le 2026-10-15 sur un vrai papier de cabinet converti par LibreOffice :
 * 1. LibreOffice place une zone de texte de pied de page (groupe ancré à la page) en HAUT d'un
 *    document d'une seule page, et à sa place dès qu'il en a deux. Le modèle, vidé, n'a qu'un
 *    paragraphe : l'adresse du cabinet s'affichait par-dessus son logo. Le Word stocké n'est pas
 *    en cause (pied identique octet pour octet à celui du courrier, qui se rend bien).
 * 2. Un modèle a souvent une première page distincte (`w:titlePg`) : la seconde page montre
 *    l'en-tête et le pied des pages suivantes, ce que l'avocat veut aussi vérifier.
 *
 * Le corps est remplacé, la mise en page finale (`w:sectPr`) est gardée telle quelle.
 */
export async function pageDApercuDuModele(papier: ArrayBuffer): Promise<ArrayBuffer> {
  let modele: JSZip;
  try {
    modele = await JSZip.loadAsync(new Uint8Array(papier));
  } catch (error) {
    throw new GreffeDuModeleImpossibleError(
      `Le papier à en-tête n'est pas une archive Word lisible (${error instanceof Error ? error.message : String(error)}).`,
    );
  }
  const corps = lire(await texteDe(modele, DOCUMENT, 'Le papier à en-tête'), `${DOCUMENT} du modèle`);
  const miseEnPage = sectionFinale(corps, 'Le papier à en-tête');
  const racine = miseEnPage.parentElement!;
  for (const enfant of [...racine.childNodes]) {
    if (enfant !== miseEnPage) racine.removeChild(enfant);
  }
  const paragraphe = (): Element => corps.createElementNS(NS_W, 'w:p');
  const saut = paragraphe();
  const course = corps.createElementNS(NS_W, 'w:r');
  const br = corps.createElementNS(NS_W, 'w:br');
  br.setAttributeNS(NS_W, 'w:type', 'page');
  course.appendChild(br);
  saut.appendChild(course);
  for (const bloc of [paragraphe(), saut, paragraphe()]) racine.insertBefore(bloc, miseEnPage);
  modele.file(DOCUMENT, ecrire(corps));
  return modele.generateAsync({ type: 'arraybuffer' });
}
